import widgetDataTypes from '@/enum/widgetDataTypes'
import { WidgetExtended } from '@/types/dashboard'
import { DefaultCardCounterStatus } from '@/enum/redesignStatusTypes'
import set from 'lodash/set'
import get from 'lodash/get'
import i18n from '@/i18n'
import { getSelectedAccountStatusTextById } from '@/composables/globalData/useEntitiesData'
import { WIDGET_CONTENT_ALIGNMENT } from '@/enum/default-layout-config'
import { AVERAGE_DURATION_ALL_CALLS, CALLERS_TYPE_CONFIG, types } from '@/enum/queueCounters'
import { VOICEBOT_ACTIVE_CALLS_CONFIG } from '@/enum/voicebotCounters'
import { IWidgetTemplateListItem } from '@/types/templates'
import { DEFAULT_TIME_FORMAT, DEFAULT_DATE_FORMAT } from '@/enum/dateTimeFormats'

const setupCounterWidgetRequiredData = (widget: WidgetExtended | IWidgetTemplateListItem) => {
    const defaultStatus = DefaultCardCounterStatus
    if (!widget.WidgetLayout) {
        widget.WidgetLayout = {}
    }
    widget.WidgetLayout.ContentTitle = widget.WidgetLayout.ContentTitle || {}

    widget.WidgetLayout.contentAlignment = widget.WidgetLayout.contentAlignment ?? WIDGET_CONTENT_ALIGNMENT
    if (widget.WidgetLayout.status === undefined) {
        const defaultText = i18n.global.t(getSelectedAccountStatusTextById(defaultStatus))
        const text = get(widget.WidgetLayout, `ContentTitle[${defaultStatus}]`, defaultText)
        widget.WidgetLayout.status = defaultStatus
        set(widget.WidgetLayout, `ContentTitle[${defaultStatus}]`, text)
    }
    const contentTitle = get(widget.WidgetLayout, `ContentTitle[${widget.WidgetLayout.status}]`)
    if (!contentTitle && widget.WidgetLayout.status) {
        const statusName = i18n.global.t(getSelectedAccountStatusTextById(widget.WidgetLayout.status))
        set(widget.WidgetLayout, `ContentTitle[${widget.WidgetLayout.status}]`, statusName)
    }
    if (widget.WidgetLayout.WarningMaxCounter === undefined) {
        widget.WidgetLayout.WarningMaxCounter = 0
    }
    if (widget.WidgetLayout.LimitMaxCounter === undefined) {
        widget.WidgetLayout.LimitMaxCounter = 0
    }

    widget.WidgetLayout = {
        ...widget.WidgetLayout
    }

    return widget
}

const setupQueueCounterWidgetRequiredData = (widget: WidgetExtended | IWidgetTemplateListItem) => {
    const queueType = widget.WidgetLayout?.queueType ?? CALLERS_TYPE_CONFIG.id
    if (!widget.WidgetLayout) {
        widget.WidgetLayout = {}
    }
    widget.WidgetLayout.queueType = queueType
    widget.WidgetLayout.contentAlignment = widget.WidgetLayout.contentAlignment ?? WIDGET_CONTENT_ALIGNMENT
    if (!widget.WidgetLayout.ContentTitle) {
        widget.WidgetLayout.ContentTitle = {}
    }
    if (widget.WidgetLayout.WarningMaxCounter === undefined) {
        widget.WidgetLayout.WarningMaxCounter = 0
    }
    if (widget.WidgetLayout.LimitMaxCounter === undefined) {
        widget.WidgetLayout.LimitMaxCounter = 0
    }

    widget.WidgetLayout = {
        ...widget.WidgetLayout
    }

    return widget
}

const setupVoicebotCounterWidgetRequiredData = (widget: WidgetExtended | IWidgetTemplateListItem) => {
    const voicebotType = widget.WidgetLayout?.voicebotType ?? VOICEBOT_ACTIVE_CALLS_CONFIG.id
    if (!widget.WidgetLayout) {
        widget.WidgetLayout = {}
    }
    widget.WidgetLayout.voicebotType = voicebotType
    widget.WidgetLayout.contentAlignment = widget.WidgetLayout.contentAlignment ?? WIDGET_CONTENT_ALIGNMENT
    if (!widget.WidgetLayout.ContentTitle) {
        widget.WidgetLayout.ContentTitle = {}
    }

    widget.WidgetLayout = {
        ...widget.WidgetLayout
    }

    return widget
}

const setupAvgCallsDurationWidgetRequiredData = (widget: WidgetExtended | IWidgetTemplateListItem) => {
    if (!widget.WidgetLayout) {
        widget.WidgetLayout = {}
    }
    if (!widget.WidgetLayout?.showCounter) {
        widget.WidgetLayout.showCounter = AVERAGE_DURATION_ALL_CALLS
    }
    const { showText = true, displayBorder = true } = widget.WidgetLayout

    widget.WidgetLayout = {
        ...widget.WidgetLayout,
        showText,
        displayBorder
    }

    return widget
}

export const setupClockAndDateWidgetRequiredData = (widget: WidgetExtended | IWidgetTemplateListItem) => {
    if (!widget.WidgetLayout) {
        widget.WidgetLayout = {}
    }
    if (widget.WidgetLayout.show_date === undefined) {
        widget.WidgetLayout.show_date = true
    }
    if (widget.WidgetLayout.show_time === undefined) {
        widget.WidgetLayout.show_time = true
    }
    if (widget.WidgetLayout.timeFormat === undefined) {
        widget.WidgetLayout.timeFormat = DEFAULT_TIME_FORMAT
    }
    if (widget.WidgetLayout.dateFormat === undefined) {
        widget.WidgetLayout.dateFormat = DEFAULT_DATE_FORMAT
    }

    return widget
}

// TODO: need to extend required data and implement for widget creation
export const WidgetPredefineData = {
    [widgetDataTypes.COUNTER_TYPE_ID]: setupCounterWidgetRequiredData,
    [widgetDataTypes.QUEUE_COUNTER_TYPE_ID]: setupQueueCounterWidgetRequiredData,
    [widgetDataTypes.AVERAGE_CALLS_DURATION]: setupAvgCallsDurationWidgetRequiredData,
    [widgetDataTypes.CLOCK_AND_DATE_TYPE_ID]: setupClockAndDateWidgetRequiredData,
    [widgetDataTypes.VOICEBOT_COUNTER_TYPE_ID]: setupVoicebotCounterWidgetRequiredData
}
